/**
 * Temp Trip Feasibility Logic
 * Pure functions for checking which drivers can feasibly handle temp trips.
 * - Timezone conversion (UTC → company)
 * - Eligible drivers from session
 * - LOS match (trip.los ↔ driver)
 * - Max deadhead
 * - Free window & time feasibility (ETA, load/unload)
 * - Overlap conflict: earlier trip keeps driver
 */

import { haversineDistance, distanceToEtaMinutes, getTripCoords } from './deadheadEta';
import { groupLegLetter } from './groupLeg';
import { driverViewForTrip, isFleetDriver, isFleetSession } from './fleetWindows';

const DEFAULT_LOAD_UNLOAD = { load: 15, unload: 15 };
const AVERAGE_SPEED_MPH = 35;
const MINUTES_PER_MILE = 60 / AVERAGE_SPEED_MPH;

/**
 * Normalize temp trip from camelCase (API) to snake_case for consistency with assignment data.
 */
export function normalizeTempTrip(trip) {
  if (!trip) return null;
  return {
    id: trip._id || trip.id,
    trip_id: trip.tripId || trip.trip_id || trip._id,
    schedule_time: trip.scheduleTime || trip.schedule_time,
    appointment_time: trip.appointmentTime || trip.appointment_time,
    los_id: trip.los || trip.los_id,
    space_type: trip.spaceType || trip.space_type,
    pickup_lat: trip.jobOriginLatitude ?? trip.pickup_lat,
    pickup_lng: trip.jobOriginLongitude ?? trip.pickup_lng,
    dropoff_lat: trip.jobDestinationLatitude ?? trip.dropoff_lat,
    dropoff_lng: trip.jobDestinationLongitude ?? trip.dropoff_lng,
    client_name: trip.priorityClient?.displayName ?? trip.client_name,
    trip_distance_miles: trip.milage ?? trip.trip_distance_miles ?? trip.trip_distance,
    pickup_address: trip.jobOriginAddress ?? trip.pickup_address,
    dropoff_address: trip.jobDestinationAddress ?? trip.dropoff_address,
    is_break: trip.isBreakTrip ?? trip.is_break ?? false,
  };
}

/**
 * Parse max deadhead miles from metadata or user config.
 */
function getMaxDeadheadMiles(assignmentData, userConfig = {}) {
  const md = assignmentData?.metadata?.constraints_enabled?.max_deadhead_miles;
  if (typeof md === 'number') return md;
  if (typeof md === 'string') {
    const m = parseFloat(md);
    if (!Number.isNaN(m)) return m;
  }
  const uc = userConfig?.max_deadhead_miles;
  if (typeof uc === 'number') return uc;
  if (typeof uc === 'string') {
    const m = parseFloat(uc);
    if (!Number.isNaN(m)) return m;
  }
  return 30;
}

/**
 * Get load/unload times for a space type from metadata.
 * metadata.load_unload_times can be:
 * - { space_id: { load: 15, unload: 15 } }
 * - { space_id: "title: 5 min load + 5 min unload" } (string - parse or default)
 */
function getLoadUnloadTimes(metadata, spaceTypeId) {
  const lut = metadata?.load_unload_times;
  if (!lut || typeof lut !== 'object' || typeof lut === 'string') return DEFAULT_LOAD_UNLOAD;

  const key = String(spaceTypeId || '').trim().toLowerCase();
  const raw = lut[key] ?? lut[spaceTypeId];
  if (!raw) return DEFAULT_LOAD_UNLOAD;

  if (typeof raw === 'object' && (raw.load != null || raw.unload != null)) {
    return {
      load: Number(raw.load) || 15,
      unload: Number(raw.unload) || 15,
    };
  }
  return DEFAULT_LOAD_UNLOAD;
}

/**
 * Get eligible driver IDs from session driver_eligibility.
 * Fallback to all drivers in assignmentData.drivers if driver_eligibility missing or yields none.
 */
function getEligibleDriverIds(assignmentData) {
  const allDrivers = assignmentData?.drivers || [];
  const allIds = allDrivers.map((d) => String(d.driver_id || d.id || '').trim()).filter(Boolean);

  const details = assignmentData?.driver_eligibility?.driver_details;
  if (Array.isArray(details) && details.length > 0) {
    const eligible = details.filter((d) => d.is_eligible === true).map((d) => String(d.driver_id || d.id || '').trim()).filter(Boolean);
    if (eligible.length > 0) return eligible;
  }
  return allIds;
}

/**
 * Check if driver LOS matches trip LOS.
 * Driver may have fleet_los_title, fleet_space_type_title, or fleet LOS from capacities.
 * Trip has los_id. We match by LOS ID if driver has one; otherwise use fleet_los_title / space type mapping.
 */
function driverMatchesTripLOS(driver, trip, assignmentData) {
  const tripLosId = String((trip.los_id || trip.los) || '').trim().toLowerCase();
  if (!tripLosId) return true;

  // Driver from session: may have fleet_los_title, fleet_space_type_title
  // Fleet LOS can serve trip LOS if they're compatible (e.g. stretcher can do ambulatory)
  // Simplification: if driver has fleet_los_title, we check if driver's fleet can serve this trip's LOS
  // For now: accept if driver has no LOS constraint, or if trip los matches driver's fleet LOS
  const fleetLos = (driver.fleet_los_title || driver.fleet_space_type_title || '').toString().toLowerCase();
  if (!fleetLos) return true;

  // Try to match by LOS ID - we'd need a mapping. Lacking that, accept if driver has any LOS (relaxed check)
  // In practice, driver_eligibility already filters by fleet compatibility for trips in the session.
  // Temp trips use external los ID - we assume if driver is eligible for session, they might match.
  // Stricter: require trip.los_id to be in driver's fleet LOS IDs. We don't have that mapping easily.
  // Use metadata space type mapping if available; otherwise assume match for same LOS ID or relaxed.
  const loadUnload = assignmentData?.metadata?.load_unload_times || {};
  const tripStId = String((trip.space_type || trip.spaceType) || '').trim().toLowerCase();
  const stData = loadUnload[tripStId] || Object.values(loadUnload).find((v) => v && typeof v === 'object' && String(v.los_id || v.st_id || '').toLowerCase() === tripLosId);
  if (stData && typeof stData === 'object') {
    // Fleet LOS title often matches category (AMB, WAV, Stretcher). Trip LOS maps to space type.
    const tripCategory = (stData.category || stData.los_title || '').toString().toLowerCase();
    if (tripCategory && fleetLos.includes(tripCategory)) return true;
    if (fleetLos.includes('stretcher') && ['ambulatory', 'wheelchair', 'wav'].some((c) => tripCategory.includes(c))) return true;
    if (fleetLos.includes('wav') && ['ambulatory', 'wheelchair'].some((c) => tripCategory.includes(c))) return true;
    if (fleetLos.includes('amb') || fleetLos.includes('ambulatory')) {
      if (tripCategory.includes('amb')) return true;
    }
  }

  // Relaxed: if we can't determine, allow (backend does stricter check)
  return true;
}

/**
 * Compute deadhead from driver's last location (or shift start) to trip pickup.
 */
function getDeadheadToPickup(driver, trip) {
  const coords = getTripCoords(trip);
  if (!coords) return Infinity;

  const trips = driver.trips || [];
  let fromLat, fromLng;
  if (trips.length > 0) {
    const sorted = [...trips].sort((a, b) => (a.sequence ?? 999) - (b.sequence ?? 999));
    const last = sorted[sorted.length - 1];
    if (!last?.is_break) {
      const lastCoords = getTripCoords(last);
      if (lastCoords) {
        fromLat = lastCoords.dropoffLat;
        fromLng = lastCoords.dropoffLng;
      }
    }
  }
  if (fromLat == null || fromLng == null) {
    fromLat = driver.ref_latitude ?? driver.current_latitude;
    fromLng = driver.ref_longitude ?? driver.current_longitude;
  }
  if (fromLat == null || fromLng == null) return 0;

  return haversineDistance(fromLat, fromLng, coords.pickupLat, coords.pickupLng);
}

/**
 * Build ordered time windows for a driver from their bucket trips.
 * Returns array of { start, end } in minutes-since-midnight (simplified) or Date objects.
 */
function getDriverBusyWindows(driver, timezone = 'America/Los_Angeles') {
  const trips = driver.trips || [];
  if (!trips.length) return [];

  const sorted = [...trips].sort((a, b) => {
    const sa = new Date((a.schedule_time || a.scheduleTime || '')).getTime();
    const sb = new Date((b.schedule_time || b.scheduleTime || '')).getTime();
    return sa - sb || ((a.sequence ?? 999) - (b.sequence ?? 999));
  });
  const windows = [];

  for (const t of sorted) {
    if (t.is_break) continue;
    const s = t.schedule_time || t.scheduleTime;
    if (!s) continue;
    const startMs = new Date(s).getTime();
    const lu = DEFAULT_LOAD_UNLOAD;
    const driveMin = getTripDriveMinutes(t);
    const endMs = startMs + (lu.load + driveMin + lu.unload) * 60 * 1000;
    windows.push({ start: startMs, end: endMs });
  }
  return windows;
}

/**
 * Check if a time range [tripStart - eta, tripEnd] fits in driver's free windows.
 * Driver must be free to leave early enough (ETA) to arrive at pickup by schedule time.
 */
function hasFreeWindow(driver, trip, loadUnload, assignmentData, timezone) {
  const trips = driver.trips || [];
  const busyWindows = getDriverBusyWindows(driver, timezone);

  const coords = getTripCoords(trip);
  if (!coords) return false;
  const deadheadMiles = getDeadheadToPickup(driver, trip);
  const etaToPickup = distanceToEtaMinutes(deadheadMiles);

  const s = trip.schedule_time || trip.scheduleTime;
  const a = trip.appointment_time || trip.appointmentTime;
  if (!s) return false;

  const tripStartMs = new Date(s).getTime();
  const tripApptMs = new Date(a || s).getTime();
  const driveMin = (Number(trip.trip_distance_miles || trip.trip_distance || trip.milage) || 0) * MINUTES_PER_MILE;
  const tripEndMs = tripApptMs;
  const blockStartMs = tripStartMs - etaToPickup * 60 * 1000;

  if (trips.length === 0) {
    const shiftStart = driver.shift_window?.shift_start;
    const shiftEnd = driver.shift_window?.shift_end;
    if (shiftStart && shiftEnd) {
      const startMs = new Date(shiftStart).getTime();
      const endMs = new Date(shiftEnd).getTime();
      return blockStartMs >= startMs && tripEndMs <= endMs;
    }
    return true;
  }

  busyWindows.sort((a, b) => a.start - b.start);

  const shiftStart = driver.shift_window?.shift_start;
  const shiftEnd = driver.shift_window?.shift_end;
  for (let i = 0; i <= busyWindows.length; i++) {
    const freeStart = i === 0 ? (shiftStart ? new Date(shiftStart).getTime() : 0) : busyWindows[i - 1].end;
    const freeEnd = i === busyWindows.length ? (shiftEnd ? new Date(shiftEnd).getTime() : Infinity) : busyWindows[i].start;
    if (blockStartMs >= freeStart && tripEndMs <= freeEnd) return true;
  }
  return false;
}

/**
 * Get trip drive time in minutes (pickup to dropoff). Uses haversine if trip_distance missing.
 */
function getTripDriveMinutes(trip) {
  const dist = Number(trip.trip_distance_miles ?? trip.trip_distance ?? trip.milage);
  if (dist > 0) return dist * MINUTES_PER_MILE;
  const c = getTripCoords(trip);
  if (!c) return 0;
  const miles = haversineDistance(c.pickupLat, c.pickupLng, c.dropoffLat, c.dropoffLng);
  return miles * MINUTES_PER_MILE;
}

/** Leg A, or a missing letter, is complete by appointment. Any other letter is picked up by schedule. */
function usesAppointmentDeadline(trip) {
  if (!trip || trip.is_break || trip.isBreakTrip) return false;
  const letter = groupLegLetter(trip).toUpperCase();
  return letter === '' || letter === 'A';
}

/**
 * Check time feasibility with full insertion logic.
 * Leg A: completion (arrival + load + drive + unload) must be by the appointment.
 * Other legs: arrival at pickup must be by schedule. Dropoff may slip.
 */
function checkTimeFeasibility(driver, trip, loadUnload, assignmentData) {
  const tripCoords = getTripCoords(trip);
  if (!tripCoords) return false;

  const s = trip.schedule_time || trip.scheduleTime;
  if (!s) return false;

  const driverTrips = (driver.trips || []).filter((t) => !t.is_break);
  const simulated = [...driverTrips, trip];
  simulated.sort((x, y) => {
    const sx = new Date((x.schedule_time || x.scheduleTime || '')).getTime();
    const sy = new Date((y.schedule_time || y.scheduleTime || '')).getTime();
    return sx - sy;
  });

  const tempId = trip.id || trip.trip_id || trip._id;
  const tempIdx = simulated.findIndex((t) => (t.id || t.trip_id || t._id) === tempId);
  if (tempIdx < 0) return false;

  let prevCompletionMs = null;

  for (let i = 0; i < simulated.length; i++) {
    const t = simulated[i];
    const lu = getLoadUnloadTimes(assignmentData?.metadata, t.space_type || t.spaceType);
    const tDrive = getTripDriveMinutes(t);
    const tCoords = getTripCoords(t);
    if (!tCoords) return false;

    let arrivalAtPickupMs;
    if (i === 0) {
      const shiftStart = driver.shift_window?.shift_start;
      const refLat = driver.ref_latitude ?? driver.current_latitude;
      const refLng = driver.ref_longitude ?? driver.current_longitude;
      const tScheduleMs = new Date(t.schedule_time || t.scheduleTime).getTime();
      if (refLat != null && refLng != null && shiftStart) {
        const dist = haversineDistance(refLat, refLng, tCoords.pickupLat, tCoords.pickupLng);
        const etaMin = distanceToEtaMinutes(dist);
        const baseMs = new Date(shiftStart).getTime();
        arrivalAtPickupMs = baseMs + etaMin * 60 * 1000;
      } else {
        arrivalAtPickupMs = tScheduleMs;
      }
    } else {
      const prev = simulated[i - 1];
      const prevCoords = getTripCoords(prev);
      if (!prevCoords) return false;
      const prevLu = getLoadUnloadTimes(assignmentData?.metadata, prev.space_type || prev.spaceType);
      const prevDrive = getTripDriveMinutes(prev);
      const prevCompletion = prevCompletionMs;
      const travelDist = haversineDistance(prevCoords.dropoffLat, prevCoords.dropoffLng, tCoords.pickupLat, tCoords.pickupLng);
      const travelMin = distanceToEtaMinutes(travelDist);
      arrivalAtPickupMs = prevCompletion + travelMin * 60 * 1000;
    }

    const completionMs = arrivalAtPickupMs + (lu.load + tDrive + lu.unload) * 60 * 1000;
    prevCompletionMs = completionMs;

    if (usesAppointmentDeadline(t)) {
      const tApptMs = new Date(t.appointment_time || t.appointmentTime || t.schedule_time || t.scheduleTime).getTime();
      if (completionMs > tApptMs) return false;
    } else {
      const tScheduleMs = new Date(t.schedule_time || t.scheduleTime).getTime();
      if (arrivalAtPickupMs > tScheduleMs) return false;
    }
  }

  return true;
}

/**
 * Main feasibility check. Returns Map<tempTripId, driver_id[]>
 */
function resolveDriverForTrip(driver, trip, assignmentData) {
  if (!isFleetSession(assignmentData) || !isFleetDriver(driver)) return driver;
  return driverViewForTrip(driver, trip);
}

function getRejectionReason(driver, trip, loadUnload, assignmentData, timezone, maxDeadhead) {
  const view = resolveDriverForTrip(driver, trip, assignmentData);
  if (!view) return 'Outside every fleet window';
  if (!driverMatchesTripLOS(view, trip, assignmentData)) return 'LOS mismatch';
  const deadhead = getDeadheadToPickup(view, trip);
  if (deadhead > maxDeadhead) return `Deadhead ${deadhead.toFixed(1)} mi exceeds max ${maxDeadhead} mi`;
  if (!hasFreeWindow(view, trip, loadUnload, assignmentData, timezone)) return 'No free time window';
  if (!checkTimeFeasibility(view, trip, loadUnload, assignmentData)) return 'Time feasibility failed (late or would miss next trip)';
  return null;
}

export function checkFeasibility(tempTrips, assignmentData, userConfig = {}) {
  const feasible = new Map();
  const rejected = new Map();
  if (!tempTrips?.length || !assignmentData) return { feasible, rejected };

  const timezone = assignmentData.metadata?.timezone || userConfig.la_timezone || 'America/Los_Angeles';
  const maxDeadhead = getMaxDeadheadMiles(assignmentData, userConfig);
  const eligibleIds = new Set(getEligibleDriverIds(assignmentData));
  let drivers = (assignmentData.drivers || []).filter((d) => {
    const id = String(d.driver_id || d.id || '').trim();
    return id && (eligibleIds.size === 0 || eligibleIds.has(id));
  });
  if (drivers.length === 0 && (assignmentData.drivers || []).length > 0) {
    drivers = assignmentData.drivers;
  }
  const normalizedTrips = tempTrips.filter((t) => !(t.isBreakTrip ?? t.is_break)).map(normalizeTempTrip).filter(Boolean);

  const tripToLoadUnload = {};
  for (const t of normalizedTrips) {
    tripToLoadUnload[t.id] = getLoadUnloadTimes(assignmentData.metadata, t.space_type);
  }

  const candidateMap = new Map();
  const rejectedMap = new Map();
  for (const trip of normalizedTrips) {
    const candidates = [];
    const tripRejected = new Map();
    const loadUnload = tripToLoadUnload[trip.id] || DEFAULT_LOAD_UNLOAD;

    for (const driver of drivers) {
      const reason = getRejectionReason(driver, trip, loadUnload, assignmentData, timezone, maxDeadhead);
      if (reason) {
        tripRejected.set(String(driver.driver_id || driver.id), reason);
      } else {
        candidates.push(String(driver.driver_id || driver.id));
      }
    }
    candidateMap.set(trip.id, candidates);
    rejectedMap.set(trip.id, tripRejected);
  }

  for (const [tripId, candidates] of candidateMap) {
    feasible.set(tripId, candidates);
  }
  for (const [tripId, tripRejected] of rejectedMap) {
    rejected.set(tripId, tripRejected);
  }
  return { feasible, rejected };
}
